import { useMemo } from "react";
import Spinner from "./Spinner";

// Animation status configuration
const STATUS_CONFIG = {
  completed: { icon: "🎬", color: "bg-green-500", label: "Completed" },
  processing: { icon: "⏳", color: "bg-yellow-500", label: "Processing" },
  pending: { icon: "⏸️", color: "bg-gray-300", label: "Pending" },
  failed: { icon: "❌", color: "bg-red-500", label: "Failed" },
  skipped: { icon: "⏭️", color: "bg-gray-400", label: "Skipped" },
};

export default function AnimationProgressBar({ 
  stats = {}, 
  thumbnails = [],
  onRetryFailed,
  onSkipPending,
}) {
  const { total = 0, pending = 0, processing = 0, completed = 0, failed = 0, skipped = 0 } = stats;
  
  // Calculate progress percentage
  const progressPercent = useMemo(() => {
    if (total === 0) return 0;
    return Math.round(((completed + skipped) / total) * 100);
  }, [total, completed, skipped]);

  // Check if still processing
  const isProcessing = processing > 0 || pending > 0;

  // Get failed thumbnails for retry
  const failedThumbnails = useMemo(() => 
    thumbnails.filter(t => t.animation_status === "failed"),
    [thumbnails]
  );

  if (total === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Animation Progress</h3>
        {isProcessing && (
          <span className="text-xs text-gray-500 flex items-center gap-1.5">
            <Spinner size="xs" color="text-gray-400" />
            Generating...
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div 
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-400 to-primary-500 transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
        {/* Processing indicator */}
        {processing > 0 && (
          <div 
            className="absolute inset-y-0 bg-yellow-400 animate-pulse transition-all duration-500"
            style={{ 
              left: `${((completed + skipped) / total) * 100}%`,
              width: `${(processing / total) * 100}%`
            }}
          />
        )}
      </div>

      {/* Progress text */}
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-gray-600 font-medium">
          {completed + skipped}/{total} segments
        </span>
        <span className="text-gray-500">{progressPercent}%</span>
      </div>

      {/* Status breakdown */}
      <div className="flex flex-wrap gap-3 text-xs">
        {completed > 0 && (
          <span className="flex items-center gap-1 text-green-600">
            <span>{STATUS_CONFIG.completed.icon}</span>
            <span>Completed: {completed}</span>
          </span>
        )}
        {processing > 0 && (
          <span className="flex items-center gap-1 text-yellow-600">
            <span>{STATUS_CONFIG.processing.icon}</span>
            <span>Processing: {processing}</span>
          </span>
        )}
        {pending > 0 && (
          <span className="flex items-center gap-1 text-gray-500">
            <span>{STATUS_CONFIG.pending.icon}</span>
            <span>Pending: {pending}</span>
          </span>
        )}
        {failed > 0 && (
          <span className="flex items-center gap-1 text-red-600">
            <span>{STATUS_CONFIG.failed.icon}</span>
            <span>Failed: {failed}</span>
          </span>
        )}
        {skipped > 0 && (
          <span className="flex items-center gap-1 text-gray-500">
            <span>{STATUS_CONFIG.skipped.icon}</span>
            <span>Skipped: {skipped}</span>
          </span>
        )}
      </div>

      {/* Action buttons for failed animations */}
      {failed > 0 && onRetryFailed && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-red-600">
              {failed} animation{failed > 1 ? "s" : ""} failed
            </span>
            <button
              onClick={() => onRetryFailed(failedThumbnails)}
              className="px-3 py-2 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors min-h-[44px] flex items-center"
            >
              🔄 Retry Failed
            </button>
          </div>
        </div>
      )}

      {/* Skip all pending option */}
      {pending > 0 && onSkipPending && !isProcessing && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={onSkipPending}
            className="text-xs text-gray-500 hover:text-gray-700 min-h-[44px] flex items-center"
          >
            ⏭️ Skip remaining animations
          </button>
        </div>
      )}
    </div>
  );
}
